import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Code, Zap, Repeat, Globe, Monitor, Sliders, Video, Layers, DollarSign, Play, UserCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

const FeaturesAndBenefitsReAX = () => {
  const { t } = useLanguage();

  const keyFeatures = [
    {
      icon: <UserCheck className="h-8 w-8 text-[#069E50]" />,
      title: "Zero Programming Required",
      description: "Built specifically for AV integrators without coding background. Create professional control interfaces using simple drag-and-drop tools."
    },
    {
      icon: <Code className="h-8 w-8 text-[#069E50]" />,
      title: "Visual Interface Builder",
      description: "Intuitive drag-and-drop interface means anyone can build control GUIs. No scripting, no coding, no programming knowledge needed."
    },
    {
      icon: <Zap className="h-8 w-8 text-[#069E50]" />,
      title: "AI Does the Programming for You",
      description: "Simply input what you want to control, and AI automatically generates all the JavaScript logic. Perfect for non-programmers who want advanced functionality."
    },
    {
      icon: <Repeat className="h-8 w-8 text-[#069E50]" />,
      title: "Efficient Project Design",
      description: "Easily duplicate, reuse, and repurpose pre-built control blocks across jobs to save valuable time and effort."
    },
    {
      icon: <Globe className="h-8 w-8 text-[#069E50]" />,
      title: "Runs On Anything",
      description: "Our Core Studio Software is HTML based. This means it will run on anything that has web browser capability. We have touchscreens with full IR, RS232, LAN and I/O ports that will run standalone, as well as controllers for the more traditional installer. Both options can even be mixed! Major manufacturers already have Core Studio as an option in their products due to its size and flexibility."
    },
    {
      icon: <Monitor className="h-8 w-8 text-[#069E50]" />,
      title: "Full-Screen Web App Support",
      description: "Launch control interfaces on iOS, Android, or Chrome without needing a native app installation."
    },
    {
      icon: <Sliders className="h-8 w-8 text-[#069E50]" />,
      title: "Complete Control Toolkit",
      description: "Includes buttons, toggles, sliders, menus, spin boxes, icons, and more for comprehensive control solutions."
    },
    {
      icon: <Video className="h-8 w-8 text-[#069E50]" />,
      title: "AV Integration Ready",
      description: "Add live video preview windows from Aurora's AV over IP gear directly into your control UI."
    },
    {
      icon: <Layers className="h-8 w-8 text-[#069E50]" />,
      title: "Web Content Embedding",
      description: "Include real-time info like weather, stock tickers, or dashboards via HTML iframes."
    },
    {
      icon: <DollarSign className="h-8 w-8 text-[#069E50]" />,
      title: "Truly Free",
      description: "No license fees. No registration. No certifications required. Start using it today at no cost."
    }
  ];

  const deviceCompatibility = [
    "Aurora's RXT touch panels",
    "iPads and tablets",
    "Smartphones",
    "PC browsers",
    "Standalone touchscreens with IR, RS232, LAN and I/O ports",
    "Traditional controllers",
    "Any device with web browser capability",
    "Third-party manufacturer products (integrated)"
  ];

  const benefits = [
    "Perfect for AV integrators with zero programming experience",
    "Visual drag-and-drop tools eliminate the need for coding skills",
    "AI-powered automation handles all technical programming automatically",
    "Reusable control blocks increase efficiency across multiple projects",
    "Universal compatibility ensures your solutions work everywhere",
    "No ongoing costs or licensing fees",
    "Built on proven YIPI and WOWE foundation with modern improvements",
    "Live video integration capabilities with Aurora AV over IP products",
    "Web-based deployment eliminates app store dependencies",
    "Comprehensive control element library for any application",
    "HTML-based architecture allows integration into any manufacturer's products"
  ];

  const handleViewProduct = () => {
    window.scrollTo(0, 0);
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-8">
        <Link
          to="/reax-options"
          className="mb-8 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to ReAX Control Solutions
        </Link>
        <h1 className="mb-4 text-4xl font-bold tracking-tight">
          ReAX™ Core Studio AI - Features & Benefits
        </h1>
        <h2 className="text-xl text-muted-foreground mb-4">
          Professional Control Solutions Made Simple – No Programming Skills Required
        </h2>
        <p className="text-lg text-muted-foreground">
          As an AV integrator, you need powerful tools that don't slow you down. ReAX™ Core Studio AI delivers professional control solutions without requiring any programming expertise whatsoever.
        </p>
      </div>

      {/* No Programming Callout */}
      <div className="mb-16">
        <div className="bg-gradient-to-r from-[#069E50]/20 to-[#069E50]/10 rounded-lg p-8 border-2 border-[#069E50]/30">
          <div className="flex items-start gap-4">
            <UserCheck className="h-12 w-12 text-[#069E50] mt-1 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold mb-3 text-[#069E50]">No Programming Experience? No Problem!</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                ReAX™ Core Studio AI is specifically designed for AV professionals who don't have coding skills. 
                Our visual drag-and-drop interface and AI-powered automation handle all the technical complexity, 
                so you can focus on what you do best – creating amazing AV experiences for your clients.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Why AV Pros Love Core Studio (No Coding Required!)</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {keyFeatures.map((feature, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Device Compatibility Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Universal Device Compatibility</h2>
        <div className="bg-card rounded-lg p-8 border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-[#069E50]">HTML-Based Control Runs On:</h3>
          <p className="text-muted-foreground mb-6">
            Because our software is HTML-based, it doesn't require dedicated controllers. Major manufacturers choose to integrate our control system into their products for this very reason.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {deviceCompatibility.map((device, index) => (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-[#069E50] flex-shrink-0" />
                <span className="text-muted-foreground">{device}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Technical Foundation Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Technical Foundation</h2>
        <div className="bg-gradient-to-r from-[#069E50]/10 to-[#069E50]/5 rounded-lg p-8 border border-[#069E50]/20">
          <div className="flex items-start gap-4">
            <Layers className="h-8 w-8 text-[#069E50] mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-semibold mb-3 text-[#069E50]">Familiar for Current Users</h3>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Built on proven YIPI and WOWE foundations with a refreshed UI and modern code using Node.js for improved performance. 
                Current users will find the transition seamless while enjoying enhanced capabilities and better performance – all without needing to learn programming.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Business Benefits Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Business Benefits for Non-Programmers</h2>
        <div className="bg-card rounded-lg p-8 border shadow-sm">
          <div className="grid gap-4 md:grid-cols-2">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#069E50] mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Getting Started Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Get Started Fast (No Technical Background Required)</h2>
        <div className="bg-card rounded-lg p-8 border shadow-sm">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="flex items-start gap-4">
              <Play className="h-6 w-6 text-[#069E50] mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold mb-2">Training Resources</h3>
                <p className="text-muted-foreground mb-3">Watch training videos designed for non-programmers and join live or recorded webinars for beginner to advanced walkthroughs.</p>
                <a 
                  href="https://auroramultimedia.com/video-category/how-to-videos" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#069E50] hover:underline"
                >
                  View How-To Videos
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Globe className="h-6 w-6 text-[#069E50] mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold mb-2">Customer Portal Access</h3>
                <p className="text-muted-foreground">Download ReAX™ Core Studio AI today from our Customer Portal at{" "}
                  <a 
                    href="https://auroramultimedia.com/portal" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#069E50] hover:underline"
                  >
                    auroramultimedia.com/portal
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center bg-muted rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-4">Ready to Create Professional Control Solutions Without Programming?</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          Discover the complete ReAX Control product range and start building professional control solutions today – no coding skills required.
        </p>
        <Link to="/reax-options" onClick={handleViewProduct}>
          <Button size="lg">
            View ReAX Control Solutions
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default FeaturesAndBenefitsReAX;
